import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, LogOut, Mail, MessageSquare } from "lucide-react";
import { FocusEditor } from "./FocusEditor";
import { FocusDetails } from "./FocusDetails";

const Row = ({ lead, d, state, onClick }) => {
  const done = state === "reviewed";
  return (
    <button onClick={onClick} data-testid={`focus-row-${lead.id}`}
      className={`w-full text-left flex items-center gap-3 px-3 py-2 rounded-xl border text-sm transition-colors ${done ? "bg-emerald-50 border-emerald-100" : state === "skipped" ? "border-slate-100 text-slate-400" : "border-slate-100 hover:bg-slate-50"}`}>
      {done ? <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0"><Check className="w-3 h-3" /></span>
        : <span className="w-5 h-5 rounded-full border-2 border-slate-300 shrink-0" />}
      <b className="text-slate-800 shrink-0">{lead.name || "Unknown"}</b>
      <span className="text-xs text-slate-500 truncate min-w-0">{(d?.sms_ok && d.text) || lead.service || ""}</span>
      <span className={`ml-auto text-[11px] font-bold shrink-0 ${done ? "text-emerald-700" : "text-slate-400"}`}>
        {done ? (d?.edited ? "Edited · Reviewed" : "Reviewed") : state === "skipped" ? "Skipped" : "To do"}
      </span>
    </button>
  );
};

// Full-screen review workspace: working queue + draft editor on the left, lead details on the right.
export const FocusMode = ({ mode, queue, drafts, ensure, onUpdate, gingrUrl, onExit }) => {
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState({});
  const [edited, setEdited] = useState(0);
  const editor = useRef(null);
  const cur = queue[idx];
  const d = cur && drafts[cur.id];
  const reviewed = Object.values(state).filter((s) => s === "reviewed").length;
  const skipped = Object.values(state).filter((s) => s === "skipped").length;

  useEffect(() => { ensure(queue.slice(idx, idx + 4).map((l) => l.id)); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  useEffect(() => {
    document.getElementById(`focus-row-${queue[idx - 1]?.id}`)?.scrollIntoView({ block: "nearest" });
  }, [idx, queue]);

  const next = () => setIdx((i) => Math.min(i + 1, queue.length));
  const exit = () => onExit({ reviewed, skipped, edited });
  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName);
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); editor.current?.save(); return; }
      if (e.key === "Escape") { exit(); return; }
      if (typing) return;
      if (e.key === "ArrowDown") { e.preventDefault(); next(); }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
      if (e.key.toLowerCase() === "s") { setState((s) => ({ ...s, [cur?.id]: s[cur?.id] || "skipped" })); next(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const saved = (patch, wasEdited) => {
    onUpdate(cur.id, patch);
    setState((s) => ({ ...s, [cur.id]: "reviewed" }));
    if (wasEdited) setEdited((x) => x + 1);
    next();
  };
  const pct = queue.length ? Math.round(((reviewed + skipped) / queue.length) * 100) : 0;
  const done = idx >= queue.length;

  return (
    <div className="fixed inset-0 z-[60] bg-slate-100 flex flex-col" data-testid="focus-mode">
      <div className="h-14 bg-slate-900 text-white flex items-center px-4 sm:px-5 gap-4 shrink-0">
        <div className="flex items-center gap-2 font-extrabold" data-testid="focus-mode-title">
          <span className={`w-2.5 h-2.5 rounded-full animate-pulse ${mode === "email" ? "bg-sky-400" : "bg-emerald-400"}`} />
          Focus mode · {mode === "email" ? <><Mail className="w-4 h-4" />Emails</> : <><MessageSquare className="w-4 h-4" />Texts</>}
        </div>
        <div className="text-sm text-slate-300 hidden sm:block" data-testid="focus-progress-text">
          Reviewing <b className="text-white">{Math.min(idx + 1, queue.length)} of {queue.length}</b> · {reviewed} reviewed · {skipped} skipped
        </div>
        <div className="flex-1 max-w-md h-2 bg-slate-700 rounded-full overflow-hidden hidden md:block"><div className="h-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        <div className="text-xs text-slate-400 hidden xl:block">
          <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">↑</kbd> <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">↓</kbd> move · <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">⌘ Enter</kbd> save &amp; next · <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">S</kbd> skip · <kbd className="px-1.5 py-0.5 bg-slate-700 rounded">Esc</kbd> exit
        </div>
        <Button size="sm" onClick={exit} className="ml-auto bg-white text-slate-900 hover:bg-slate-200 rounded-full font-bold" data-testid="focus-exit-btn">
          <LogOut className="w-3.5 h-3.5 mr-1" />{mode === "email" ? `Exit (${reviewed} reviewed)` : `Exit → review & send (${reviewed})`}
        </Button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div className="lg:w-[46%] border-r border-slate-200 bg-white flex flex-col min-h-0 max-lg:max-h-[60vh]">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-800">Working queue <span className="text-xs font-normal text-slate-500">· {mode === "email" ? "email-only leads" : "leads you can text"} · same order as your sort &amp; filters</span></div>
          {mode === "email" && <div className="mx-4 mt-3 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="focus-email-note">"Send email" goes straight to the customer from this location's Outlook (if sending is turned on in Settings). "Send to my Outlook for review" emails the draft to your own inbox instead.</div>}
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="focus-queue">
            {queue.map((l, i) => i === idx ? (
              <FocusEditor key={l.id} ref={editor} lead={l} d={d} pos={i + 1} channel={mode} onSaved={saved}
                onBack={() => setIdx((x) => Math.max(0, x - 1))}
                onSkip={() => { setState((s) => ({ ...s, [l.id]: s[l.id] || "skipped" })); next(); }} />
            ) : (
              <Row key={l.id} lead={l} d={drafts[l.id]} state={state[l.id]} onClick={() => setIdx(i)} />
            ))}
            {done && (
              <div className="rounded-2xl border-2 border-emerald-400 bg-emerald-50 p-6 text-center space-y-3" data-testid="focus-done">
                <div className="text-lg font-extrabold text-emerald-800">All {queue.length} {mode === "email" ? "emails" : "texts"} reviewed 🎉</div>
                <div className="text-sm text-emerald-700">{reviewed} reviewed · {edited} edited · {skipped} skipped</div>
                <Button onClick={exit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="focus-done-exit-btn">Exit → review &amp; send</Button>
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 min-h-0">
          {cur && !done ? <FocusDetails leadId={cur.id} gingrUrl={gingrUrl} /> : <div className="text-slate-400 text-sm p-8">Session complete.</div>}
        </div>
      </div>
    </div>
  );
};
